/**
 * CollabsBalanceLedger.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Full-screen overlay (desktop) / bottom-sheet (mobile) that opens when the
 * agency clicks the "Balance" summary card on the Collaborations page.
 *
 * Mirrors the exact design of BlitzPay's BalanceLedgerModal (doc 19):
 *   Desktop → fixed full-screen box with a two-column layout
 *               Left  : balance card + quick stats + action buttons
 *               Right : transaction history with filter tabs
 *   Mobile  → bottom-sheet Dialog (same as BlitzPay)
 *
 * Self-contained — fetches its own data so Collabs doesn't need to pass
 * ledger entries as props.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Stack, Button, IconButton, Avatar, Chip, Divider,
  Paper, Grid, CircularProgress, Tabs, Tab, Alert, Dialog, Fade,
  useMediaQuery, Skeleton,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  AccountBalanceWallet as WalletIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  AttachMoney as WithdrawIcon,
  SwapHoriz as TransferIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Campaign as CampaignIcon,
  Link as LinkIcon,
  Percent as PercentIcon,
  Paid as PaidIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import client from '../../../../API';
import AddBalanceDialog from "../../../../Components/tools/addBalance";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

/* Derive display metadata for a ledger entry (mirrors BlitzPay's getTransactionDisplay) */
function getEntryDisplay(entry) {
  const op = (entry.operation_type || '').toLowerCase();
  const amount = parseFloat(entry.change_amount || entry.amount || 0);
  const isCredit = amount > 0;
  const details = entry.details || {};

  if (op.includes('commission')) {
    return {
      icon: <CampaignIcon />,
      color: 'success',
      title: 'Commission Earned',
      subtitle: details.source_type
        ? `${details.source_type}${details.source_id ? ` #${details.source_id}` : ''}`
        : 'Commission',
      isCredit: true,
    };
  }
  if (op.includes('internal_transfer') || (entry.brief || '').toLowerCase().includes('transfer')) {
    return {
      icon: isCredit ? <TrendingUpIcon /> : <TrendingDownIcon />,
      color: isCredit ? 'success' : 'primary',
      title: isCredit ? 'Transfer Received' : 'Transfer Sent',
      subtitle: details.description || entry.reason || '',
      isCredit,
    };
  }
  if (op.includes('withdrawal') || (entry.brief || '').toLowerCase().includes('withdraw')) {
    return {
      icon: <WithdrawIcon />,
      color: 'warning',
      title: 'Withdrawal',
      subtitle: details.service ? `via ${details.service}` : '',
      isCredit: false,
    };
  }
  if (op.includes('payment') || op.includes('deposit')) {
    return {
      icon: isCredit ? <AddCircleIcon /> : <RemoveCircleIcon />,
      color: isCredit ? 'success' : 'error',
      title: isCredit ? 'Balance Funded' : 'Payment',
      subtitle: '',
      isCredit,
    };
  }
  return {
    icon: isCredit ? <UpIcon /> : <DownIcon />,
    color: isCredit ? 'success' : 'default',
    title: entry.brief || entry.operation_type?.replace(/_/g, ' ') || (isCredit ? 'Credit' : 'Debit'),
    subtitle: details.description || entry.reason || '',
    isCredit,
  };
}

function getCategory(entry) {
  const op = (entry.operation_type || '').toLowerCase();
  const brief = (entry.brief || '').toLowerCase();
  if (op.includes('commission')) return 'commission';
  if (op.includes('internal_transfer') || brief.includes('transfer')) return 'transfer';
  if (op.includes('withdrawal') || brief.includes('withdraw')) return 'withdrawal';
  if (op.includes('payment') || op.includes('deposit')) return 'payment';
  return 'other';
}

/* ── Transaction row ─────────────────────────────────────────────────────── */

const EntryRow = ({ entry, compact = false }) => {
  const theme = useTheme();
  const display = getEntryDisplay(entry);
  const amount = Math.abs(parseFloat(entry.change_amount || entry.amount || 0));

  if (compact) {
    return (
      <Paper sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: `${display.color}.light`, color: `${display.color}.dark` }}>
            {display.icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>{display.title}</Typography>
            {display.subtitle && <Typography variant="caption" color="text.secondary" noWrap display="block">{display.subtitle}</Typography>}
            <Typography variant="caption" color="text.disabled">{fmtDate(entry.created_at || entry.timestamp)}</Typography>
          </Box>
          <Typography variant="body2" fontWeight={700} color={display.isCredit ? 'success.main' : 'text.primary'}>
            {display.isCredit ? '+' : '-'}{fmt(amount)}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{
      p: 3, borderRadius: 2.5, border: `1px solid ${theme.palette.divider}`, mb: 2,
      transition: 'all 0.2s',
      '&:hover': { borderColor: `${display.color}.main`, boxShadow: 4, transform: 'translateY(-2px)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: `${display.color}.light`, color: `${display.color}.dark` }}>
          {display.icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.25 }}>{display.title}</Typography>
          {display.subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.25 }}>{display.subtitle}</Typography>}
          <Typography variant="caption" color="text.disabled">{fmtDate(entry.created_at || entry.timestamp)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" fontWeight={800} color={display.isCredit ? 'success.main' : 'text.primary'}>
            {display.isCredit ? '+' : '-'}{fmt(amount)}
          </Typography>
          {(entry.new_balance || entry.balance_after) && (
            <Typography variant="caption" color="text.secondary">
              Balance: {fmt(entry.new_balance || entry.balance_after)}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

/* ── Left panel ──────────────────────────────────────────────────────────── */

const BalancePanel = ({ balance, transitBalance, ledgerEntries, onAddBalance, onWithdraw, onTransfer, isMobile }) => {
  const theme = useTheme();
  const creditCount = ledgerEntries.filter((e) => parseFloat(e.change_amount || 0) > 0).length;
  const debitCount = ledgerEntries.filter((e) => parseFloat(e.change_amount || 0) < 0).length;

  return (
    <Box sx={{ p: isMobile ? 2 : 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Balance card */}
      <Paper elevation={0} sx={{
        p: isMobile ? 2.5 : 4, borderRadius: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white', position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', inset: 0,
          background: 'radial-gradient(circle at top right, rgba(255,255,255,0.18) 0%, transparent 65%)',
          pointerEvents: 'none',
        },
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="caption" sx={{ opacity: 0.85, fontWeight: 600, letterSpacing: '1px', display: 'block', mb: 0.5 }}>
            SPENDABLE BALANCE
          </Typography>
          <Typography variant={isMobile ? 'h4' : 'h2'} fontWeight={800} sx={{ mb: transitBalance > 0 ? 1 : 2 }}>
            {fmt(balance)}
          </Typography>
          {transitBalance > 0 && (
            <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
              + {fmt(transitBalance)} in transit
            </Typography>
          )}

          {/* Action buttons inside balance card */}
          <Grid container spacing={1}>
            <Grid item xs={isMobile ? 6 : 12}>
              <Button fullWidth variant="contained" startIcon={<AddCircleIcon />} onClick={onAddBalance}
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                Add Funds
              </Button>
            </Grid>
            <Grid item xs={isMobile ? 6 : 12}>
              <Button fullWidth variant="contained" startIcon={<WithdrawIcon />} onClick={onWithdraw}
                sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                Withdraw
              </Button>
            </Grid>
            {!isMobile && (
              <Grid item xs={12}>
                <Button fullWidth variant="contained" startIcon={<TransferIcon />} onClick={onTransfer}
                  sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.32)' }, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}>
                  Transfer to User
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>
      </Paper>

      {/* Quick stats */}
      {!isMobile && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'success.light', color: 'success.dark' }}><TrendingUpIcon fontSize="small" /></Avatar>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>CREDITS</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700}>{creditCount}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: `1px solid ${theme.palette.divider}` }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.75 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'error.light', color: 'error.dark' }}><TrendingDownIcon fontSize="small" /></Avatar>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>DEBITS</Typography>
              </Stack>
              <Typography variant="h6" fontWeight={700}>{debitCount}</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

/* ── Main export ─────────────────────────────────────────────────────────── */

/**
 * CollabsBalanceLedger
 *
 * Props:
 *   open     boolean
 *   onClose  () => void
 */
export default function CollabsBalanceLedger({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filterTab, setFilterTab] = useState(0);
  const [showAddBalance, setShowAddBalance] = useState(false);

  /* ── Data ── */
  const { data: balanceData, isLoading: balanceLoading, refetch: refetchBalance } = useQuery(
    ['balance', 'my_balance'],
    () => client.balanceLedger.fetchMyBalance(),
    { refetchOnWindowFocus: false, staleTime: 1000 * 60 * 5, enabled: open }
  );

  const effectiveUserId = balanceData?.user?.id;

  const {
    data: ledgerRaw, isLoading: ledgerLoading, refetch: refetchLedger,
  } = useQuery(
    ['balanceLedgerHistory', effectiveUserId, 'collabs'],
    () => client.balanceLedger.fetch(effectiveUserId),
    {
      enabled: !!effectiveUserId && open,
      refetchOnWindowFocus: false,
      staleTime: 30000,
      select: (data) => {
        if (Array.isArray(data)) return data;
        return data?.entries || data?.ledger || data?.data || data?.transactions || [];
      },
    }
  );

  const {
    data: withdrawalData, isLoading: withdrawalLoading,
  } = useQuery(
    ['withdrawalHistory', 'collabs'],
    () => client.withdrawals.userGetHistory({ limit: 50 }),
    { enabled: open, refetchOnWindowFocus: false, staleTime: 30000 }
  );

  // Force-refetch when opening
  useEffect(() => {
    if (open && effectiveUserId) {
      refetchBalance();
      refetchLedger();
    }
  }, [open]);

  const balance = parseFloat(balanceData?.balance || 0);
  const transitBalance = parseFloat(balanceData?.transit_balance || 0);
  const ledgerEntries = ledgerRaw || [];

  // Balance withdrawals (exclude payouts — same filter as BlitzPay)
  const balanceWithdrawals = useMemo(() => {
    return (withdrawalData?.withdrawals || []).filter((w) =>
      !(w.brief || '').toUpperCase().includes('PAYOUT')
    );
  }, [withdrawalData]);

  const isLoading = balanceLoading || ledgerLoading;

  /* ── Filter tabs ── */
  const categoryCount = (cat) => {
    if (cat === 'withdrawal') return balanceWithdrawals.length;
    return ledgerEntries.filter((e) => getCategory(e) === cat).length;
  };

  const filteredEntries = useMemo(() => {
    if (filterTab === 0) return ledgerEntries;
    const catMap = { 1: 'transfer', 2: 'payment', 4: 'commission' };
    const cat = catMap[filterTab];
    if (!cat) return ledgerEntries;
    return ledgerEntries.filter((e) => getCategory(e) === cat);
  }, [ledgerEntries, filterTab]);

  /* ── Shared handlers ── */
  const handleRefresh = () => {
    queryClient.invalidateQueries(['balance', 'my_balance']);
    queryClient.invalidateQueries(['balanceLedgerHistory']);
    refetchBalance();
    refetchLedger();
  };

  const handleWithdraw = () => {
    onClose();
    navigate('/dashboard');
  };

  const handleTransfer = () => {
    onClose();
    navigate('/blitzpay/internal-transfer');
  };

  /* ================================================================= */
  /* MOBILE — bottom sheet Dialog (mirrors BlitzPay mobile)            */
  /* ================================================================= */

  const APPSHELL = 86;

  if (isMobile) {
    return (
      <>
        <Dialog
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              borderRadius: '24px 24px 0 0',
              maxHeight: `calc(100vh - ${APPSHELL}px)`,
              m: 0, width: '100%', maxWidth: '100%',
              position: 'fixed', bottom: `${APPSHELL}px`, left: 0, right: 0,
            },
          }}
          sx={{ zIndex: theme.zIndex.modal + 1, '& .MuiDialog-container': { alignItems: 'flex-end' } }}
        >
          {/* Handle + header */}
          <Box sx={{ pt: 2.5, pb: 1, px: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ width: 40, height: 4, bgcolor: 'divider', borderRadius: 2, mx: 'auto', mb: 2 }} />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 36, height: 36, borderRadius: 2, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <WalletIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>Blitz Balance</Typography>
                  <Typography variant="caption" color="text.secondary">Agency financial overview</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleRefresh}><RefreshIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            </Stack>
          </Box>

          {/* Scrollable content */}
          <Box sx={{ overflowY: 'auto', px: 2, pb: 2, flex: 1 }}>
            <BalancePanel
              balance={balance} transitBalance={transitBalance}
              ledgerEntries={ledgerEntries}
              onAddBalance={() => setShowAddBalance(true)}
              onWithdraw={handleWithdraw}
              onTransfer={handleTransfer}
              isMobile
            />

            {isLoading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1 }} />)
            ) : ledgerEntries.length > 0 ? (
              <>
                <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
                  <Tab label={`All (${ledgerEntries.length})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }} />
                  <Tab label={`Transfers (${categoryCount('transfer')})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }} />
                  <Tab label={`Payments (${categoryCount('payment')})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }} />
                  <Tab label={`Withdrawals (${balanceWithdrawals.length})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }} />
                  <Tab label={`Commissions (${categoryCount('commission')})`} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem' }} />
                </Tabs>

                {filterTab === 3 ? (
                  balanceWithdrawals.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No withdrawals yet</Typography>
                  ) : (
                    balanceWithdrawals.slice(0, 20).map((w) => (
                      <Paper key={w.id} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'warning.light', color: 'warning.dark' }}><WithdrawIcon /></Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={600}>Withdrawal</Typography>
                            <Typography variant="caption" color="text.secondary">{fmtDate(w.created_at)}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700}>-{fmt(w.amount_no_fee || w.amount)}</Typography>
                        </Stack>
                      </Paper>
                    ))
                  )
                ) : (
                  filteredEntries.slice(0, 25).map((e) => <EntryRow key={e.id} entry={e} compact />)
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <WalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary">No transaction history yet</Typography>
              </Box>
            )}
          </Box>

          <Divider />
          <Box sx={{ p: 2 }}>
            <Button fullWidth variant="contained" onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>Close</Button>
          </Box>
        </Dialog>

        <AddBalanceDialog
          open={showAddBalance}
          onClose={() => {
            setShowAddBalance(false);
            handleRefresh();
          }}
        />
      </>
    );
  }

  /* ================================================================= */
  /* DESKTOP — full-screen fixed overlay (mirrors BlitzPay desktop)    */
  /* ================================================================= */

  return (
    <>
      <Fade in={open} timeout={260}>
        <Box sx={{
          position: 'fixed', inset: 0,
          bgcolor: 'background.default',
          zIndex: theme.zIndex.modal + 1,
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
        }}>
          {/* ── Top bar ── */}
          <Box sx={{
            height: 72, flexShrink: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 4, bgcolor: 'background.paper',
          }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{
                width: 44, height: 44, borderRadius: 2.5, flexShrink: 0,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <WalletIcon sx={{ color: 'white', fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>Your Blitz Balance</Typography>
                <Typography variant="body2" color="text.secondary">Agency financial overview & transaction history</Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={() => { onClose(); navigate('/dashboard'); }}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Open in BlitzPay
              </Button>
              <IconButton onClick={onClose} sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* ── Body ── */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Left column — balance + actions */}
            <Box sx={{
              width: 400, flexShrink: 0,
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              overflowY: 'auto',
            }}>
              {balanceLoading ? (
                <Box sx={{ p: 4 }}>
                  <Skeleton variant="rounded" height={240} sx={{ mb: 2 }} />
                  <Skeleton variant="rounded" height={100} />
                </Box>
              ) : (
                <BalancePanel
                  balance={balance} transitBalance={transitBalance}
                  ledgerEntries={ledgerEntries}
                  onAddBalance={() => setShowAddBalance(true)}
                  onWithdraw={handleWithdraw}
                  onTransfer={handleTransfer}
                  isMobile={false}
                />
              )}
            </Box>

            {/* Right column — transaction history */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                  <CircularProgress size={44} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading transaction history…</Typography>
                </Box>
              ) : (
                <>
                  {/* Filter tabs */}
                  {ledgerEntries.length > 0 && (
                    <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 4, pt: 1.5, flexShrink: 0 }}>
                      <Tabs
                        value={filterTab}
                        onChange={(_, v) => setFilterTab(v)}
                        sx={{
                          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', minHeight: 52, px: 2.5 },
                        }}
                      >
                        <Tab label={`All (${ledgerEntries.length})`} />
                        <Tab label={`Transfers (${categoryCount('transfer')})`} />
                        <Tab label={`Payments (${categoryCount('payment')})`} />
                        <Tab label={`Withdrawals (${balanceWithdrawals.length})`} />
                        <Tab label={`💰 Commissions (${categoryCount('commission')})`} sx={{ color: 'success.main' }} />
                      </Tabs>
                    </Box>
                  )}

                  {/* Entry list */}
                  <Box sx={{ flex: 1, overflowY: 'auto', px: 4, py: 3 }}>
                    {filterTab === 3 ? (
                      /* Withdrawals tab */
                      balanceWithdrawals.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 10 }}>
                          <WithdrawIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" fontWeight={600}>No Withdrawals Yet</Typography>
                          <Typography variant="body2" color="text.disabled">Your withdrawal history will appear here.</Typography>
                        </Box>
                      ) : (
                        balanceWithdrawals.map((w) => {
                          const status = w.status || 'pending';
                          const statusColor = status === 'paid' ? 'success' : status === 'processing' ? 'info' : status === 'failed' || status === 'cancelled' ? 'error' : 'warning';
                          return (
                            <Paper key={w.id} sx={{ p: 3, borderRadius: 2.5, border: `1px solid ${theme.palette.divider}`, mb: 2, transition: 'all 0.2s', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                <Avatar sx={{ width: 52, height: 52, bgcolor: `${statusColor}.light`, color: `${statusColor}.dark` }}><WithdrawIcon sx={{ fontSize: 26 }} /></Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="h6" fontWeight={700}>Withdrawal</Typography>
                                  <Typography variant="body2" color="text.secondary">{w.payment_service || 'Wallet'} · {status.charAt(0).toUpperCase() + status.slice(1)}</Typography>
                                  <Typography variant="caption" color="text.disabled">{fmtDate(w.created_at)}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="h6" fontWeight={800}>-{fmt(w.amount_no_fee || w.amount)}</Typography>
                                  <Chip label={status} size="small" color={statusColor} sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize' }} />
                                </Box>
                              </Box>
                            </Paper>
                          );
                        })
                      )
                    ) : filteredEntries.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 10 }}>
                        <WalletIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" fontWeight={600}>No transactions found</Typography>
                        <Typography variant="body2" color="text.disabled">Transactions in this category will appear here.</Typography>
                      </Box>
                    ) : (
                      filteredEntries.map((entry) => <EntryRow key={entry.id} entry={entry} />)
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>

      <AddBalanceDialog
        open={showAddBalance}
        onClose={() => {
          setShowAddBalance(false);
          handleRefresh();
        }}
      />
    </>
  );
}